// src/components/CursusEtExperiences.tsx
import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
    Box,
    Typography,
    Paper,
    Button,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    StepConnector,
    stepConnectorClasses,
} from '@mui/material';

interface CursusEtExperiencesProps {
    language: 'en' | 'fr';
}

// =======================
// 1. CONNECTEUR PERSONNALISÉ
// =======================
const ColorfulConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 22,
    },
    [`& .${stepConnectorClasses.line}`]: {
        borderColor: '#FF6200',
        borderWidth: 3,
        borderRadius: 1,
        marginLeft: '20px',
        marginRight: '20px',
    },
}));

// =======================
// 2. DONNÉES BILINGUES
// =======================
const cursusStepsData = {
    en: [
        {
            label: 'IUT Clermont-Ferrand (2023-2026)',
            description: `Currently a second-year student in the BUT Computer Science program at IUT Clermont-Ferrand.
I am developing my skills in programming, algorithms, databases, and project management.`,
        },
        {
            label: "Lycée Jeanne d'Arc (2020-2023) – High School Diploma with Honors",
            description: `Baccalaureate with majors in Mathematics and NSI.
I acquired a strong foundation in algorithms and programming while developing my analytical and problem-solving skills.`,
        },
    ],
    fr: [
        {
            label: 'IUT Clermont-Ferrand (2023-2026)',
            description: `Actuellement étudiant en deuxième année de BUT Informatique à l'IUT Clermont-Ferrand. 
Je développe mes compétences en programmation, algorithmique, base de données et gestion de projets.`,
        },
        {
            label: "Lycée Jeanne d'Arc (2020-2023) – Bac mention bien",
            description: `Baccalauréat spécialités Mathématiques et NSI.
J'ai acquis de solides bases en algorithmique et développement,
tout en développant mes compétences d'analyse et de résolution de problèmes.`,
        },
    ],
};

const experiencesStepsData = {
    en: [
        {
            label: 'Security Guard – Mairie Pont-du-Château (2022)',
            description: `Managed access during sports events, coordinated with local authorities,
set up control points and handled emergency situations.`,
        },
        {
            label: 'Masonry Internship – Da Silva Construction (2021)',
            description: `Observed the role of a company manager and project management in masonry,
handled client-supplier relations and team organization.`,
        },
        {
            label: 'Scientific Internship – Institut Pascal (2020)',
            description: `Explored laboratory research, data analysis, interdisciplinary collaboration,
and participated in scientific meetings.`,
        },
    ],
    fr: [
        {
            label: 'Employé sécurité – Mairie Pont-du-Château (2022)',
            description: `Sécurisation des accès lors d'événements sportifs, coordination avec les autorités locales,
installation de points de contrôle et gestion des urgences.`,
        },
        {
            label: 'Stage maçonnerie – Da Silva Construction (2021)',
            description: `Observation du métier de chef d'entreprise et gestion de projets en maçonnerie,
relation clients-fournisseurs et organisation d'équipe.`,
        },
        {
            label: 'Stage scientifique – Institut Pascal (2020)',
            description: `Découverte de la recherche en laboratoire, analyse de données,
collaboration interdisciplinaire et participation à des réunions scientifiques.`,
        },
    ],
};

// =======================
// 3. STYLED COMPONENTS & RESPONSIVE LAYOUT
// =======================

// Conteneur principal avec fond adapté et responsive
const Container = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
    maxWidth: 1200,
    margin: '40px auto',
}));

// Titre principal avec ajustement responsive
const MainTitle = styled(Typography)(({ theme }) => ({
    textAlign: 'center',
    marginBottom: theme.spacing(4),
    color: theme.palette.text.primary,
    fontWeight: 'bold',
    [theme.breakpoints.down('sm')]: {
        fontSize: '1.8rem',
    },
}));

// Conteneur pour les deux colonnes (Expériences et Cursus)
const ColumnsContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
    },
}));

// =======================
// 4. COMPOSANT PRINCIPAL
// =======================
export default function CursusEtExperiences({ language }: CursusEtExperiencesProps) {
    const theme = useTheme();

    // Textes de l'interface
    const texts = {
        en: {
            mainTitle: "My Education and Experiences",
            experiencesTitle: "My Professional Experiences",
            cursusTitle: "My Education",
            continue: "Continue",
            finish: "Finish",
            back: "Back",
            reset: "Reset",
            completedExperiences: "All experience steps are completed!",
            completedCursus: "All education steps are completed!",
        },
        fr: {
            mainTitle: "Mon Cursus et Mes Expériences",
            experiencesTitle: "Mes Expériences Professionnelles",
            cursusTitle: "Mon Cursus",
            continue: "Continuer",
            finish: "Terminer",
            back: "Retour",
            reset: "Réinitialiser",
            completedExperiences: "Toutes les étapes d'expériences sont terminées !",
            completedCursus: "Toutes les étapes du cursus sont terminées !",
        },
    };

    const {
        mainTitle: titleText,
        experiencesTitle,
        cursusTitle,
        continue: continueText,
        finish,
        back,
        reset,
        completedExperiences,
        completedCursus,
    } = texts[language];

    // Sélection des données en fonction de la langue
    const cursusSteps = cursusStepsData[language];
    const experiencesSteps = experiencesStepsData[language];

    // États pour les steppers
    const [activeCursus, setActiveCursus] = React.useState(0);
    const [activeExperience, setActiveExperience] = React.useState(0);

    // Fonctions de navigation pour le Cursus
    const handleNextCursus = () => setActiveCursus(prev => prev + 1);
    const handleBackCursus = () => setActiveCursus(prev => prev - 1);
    const handleResetCursus = () => setActiveCursus(0);

    // Fonctions de navigation pour les Expériences
    const handleNextExperience = () => setActiveExperience(prev => prev + 1);
    const handleBackExperience = () => setActiveExperience(prev => prev - 1);
    const handleResetExperience = () => setActiveExperience(0);

    return (
        <Container>
            <MainTitle variant="h4">{titleText}</MainTitle>
            <ColumnsContainer>
                {/* Expériences Professionnelles */}
                <Box sx={{ flex: 1 }}>
                    <Typography
                        variant="h5"
                        sx={{
                            mb: 2,
                            textAlign: 'center',
                            color: '#FF6200', // Couleur fixe ou adaptative via theme.palette si souhaité
                            fontWeight: 'bold',
                        }}
                    >
                        {experiencesTitle}
                    </Typography>
                    <Stepper activeStep={activeExperience} orientation="vertical" connector={<ColorfulConnector />}>
                        {experiencesSteps.map((step, index) => (
                            <Step key={step.label}>
                                <StepLabel>{step.label}</StepLabel>
                                <StepContent>
                                    <Typography sx={{ mb: 2 }}>{step.description}</Typography>
                                    <Box sx={{ mb: 2 }}>
                                        <Button
                                            variant="contained"
                                            onClick={handleNextExperience}
                                            sx={{ mt: 1, mr: 1, backgroundColor: '#FF6200' }}
                                        >
                                            {index === experiencesSteps.length - 1 ? finish : continueText}
                                        </Button>
                                        <Button
                                            disabled={index === 0}
                                            onClick={handleBackExperience}
                                            sx={{ mt: 1, mr: 1, color: '#FF6200' }}
                                        >
                                            {back}
                                        </Button>
                                    </Box>
                                </StepContent>
                            </Step>
                        ))}
                    </Stepper>
                    {activeExperience === experiencesSteps.length && (
                        <Paper square elevation={0} sx={{ p: 3 }}>
                            <Typography>{completedExperiences}</Typography>
                            <Button onClick={handleResetExperience} sx={{ mt: 1, mr: 1, backgroundColor: '#FF6200' }} variant="contained">
                                {reset}
                            </Button>
                        </Paper>
                    )}
                </Box>

                {/* Cursus Scolaire */}
                <Box sx={{ flex: 1 }}>
                    <Typography
                        variant="h5"
                        sx={{
                            mb: 2,
                            textAlign: 'center',
                            color: '#FF6200',
                            fontWeight: 'bold',
                        }}
                    >
                        {cursusTitle}
                    </Typography>
                    <Stepper activeStep={activeCursus} orientation="vertical" connector={<ColorfulConnector />}>
                        {cursusSteps.map((step, index) => (
                            <Step key={step.label}>
                                <StepLabel>{step.label}</StepLabel>
                                <StepContent>
                                    <Typography sx={{ mb: 2 }}>{step.description}</Typography>
                                    <Box sx={{ mb: 2 }}>
                                        <Button
                                            variant="contained"
                                            onClick={handleNextCursus}
                                            sx={{ mt: 1, mr: 1, backgroundColor: '#FF6200' }}
                                        >
                                            {index === cursusSteps.length - 1 ? finish : continueText}
                                        </Button>
                                        <Button
                                            disabled={index === 0}
                                            onClick={handleBackCursus}
                                            sx={{ mt: 1, mr: 1, color: '#FF6200' }}
                                        >
                                            {back}
                                        </Button>
                                    </Box>
                                </StepContent>
                            </Step>
                        ))}
                    </Stepper>
                    {activeCursus === cursusSteps.length && (
                        <Paper square elevation={0} sx={{ p: 3 }}>
                            <Typography>{completedCursus}</Typography>
                            <Button onClick={handleResetCursus} sx={{ mt: 1, mr: 1, backgroundColor: '#FF6200' }} variant="contained">
                                {reset}
                            </Button>
                        </Paper>
                    )}
                </Box>
            </ColumnsContainer>
        </Container>
    );
}
